import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { Command } from "cmdk";
import { HOTSPOTS } from "@/scene/fx/hotspotRegistry";
import { runHotspotAction } from "@/lib/actions";

export const CommandPalette = (): JSX.Element | null => {
  const [open, setOpen] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const id = requestAnimationFrame(() => inputRef.current?.focus());
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      }
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!open) return null;

  return (
    <div
      role="presentation"
      className="fixed inset-0 z-40 flex items-start justify-center bg-bg0/70 pt-32 backdrop-blur-sm"
      onClick={() => setOpen(false)}
      onKeyDown={(e) => {
        if (e.key === "Escape") setOpen(false);
      }}
    >
      <Command
        label="Command Menu"
        role="dialog"
        aria-modal="true"
        className="w-[520px] max-w-[90vw] border border-rule bg-bg1 font-mono"
        onClick={(e) => e.stopPropagation()}
      >
        <Command.Input
          ref={inputRef}
          placeholder="Type a command…"
          className="w-full border-b border-rule bg-transparent px-4 py-3 text-sm outline-none"
        />
        <Command.List className="max-h-[300px] overflow-auto p-2">
          <Command.Empty className="px-4 py-2 text-xs text-ink2">
            No matches
          </Command.Empty>
          {Object.entries(HOTSPOTS)
            .filter(([, m]) => m.action)
            .map(([key, m]) => (
              <Command.Item
                key={key}
                value={m.label}
                onSelect={() => {
                  if (m.action) runHotspotAction(m.action);
                  setOpen(false);
                }}
                className="cursor-pointer rounded px-3 py-2 text-xs uppercase tracking-wider data-[selected=true]:bg-bg2"
              >
                {m.label} · {m.shortcut?.toUpperCase() ?? ""}
              </Command.Item>
            ))}
        </Command.List>
      </Command>
    </div>
  );
};
